import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaCalendarCheck,
  FaClock,
  FaEye,
  FaSyncAlt,
  FaSearch,
  FaUserMd,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaStethoscope,
  FaVideo,
  FaPlay,
  FaCheckCircle,
  FaTimesCircle,
  FaNotesMedical,
  FaDownload,
  FaTimes,
  FaLink,
  FaUserCheck,
} from "react-icons/fa";
import AdminLayout from "../components/Layout/AdminLayout";

interface ScheduledAppointment {
  id: string;
  booking_id: string | null;

  appointmentDate: string;
  appointmentTime: string;
  rawDate: string;
  rawTime: string;

  department: string;
  status: string;
  fullName: string;
  age: string;
  dob: string;
  gender: string;
  phone: string;
  email: string;
  address: string;
  symptoms: string;

  report_url: string | null;
  payment_proof_url: string | null;

  doctor_id: string | null;
  doctor_name: string | null;
  doctor_email: string | null;

  video_link: string | null;
  link_active_from: string | null;
  link_active_until: string | null;
  canJoinNow: number;
  isLinkExpired: number;

  join_status: string | null;
  patient_join_status: string | null;
  doctor_join_status: string | null;
  patient_joined_at: string | null;
  doctor_joined_at: string | null;

  consultation_notes: string | null;
  prescription_text: string | null;
  prescription_pdf_url: string | null;
  consultation_started_at: string | null;
  consultation_ended_at: string | null;
  completed_at: string | null;

  missed_reason: string | null;
  no_show_type: string | null;

  created_at: string;
}

const ScheduledAppointments: React.FC = () => {
  const backendUrl =
    window.location.hostname === "localhost"
      ? "http://localhost:8483"
      : "https://www.ayushvatika.com";

  // const backendUrl ="http://localhost:8483";

  const [appointments, setAppointments] = useState<ScheduledAppointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchText, setSearchText] = useState("");

  const [selectedAppt, setSelectedAppt] = useState<ScheduledAppointment | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const [doctorName, setDoctorName] = useState("");
  const [doctorEmail, setDoctorEmail] = useState("");
  const [consultationNotes, setConsultationNotes] = useState("");
  const [prescriptionText, setPrescriptionText] = useState("");
  const [missedReason, setMissedReason] = useState("");

  const fetchScheduledAppointments = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("date", dateFilter || "all");
      params.append("status", statusFilter);

      if (searchText.trim()) {
        params.append("search", searchText.trim());
      }

      const response = await axios.get(
        `${backendUrl}/api/admin/appointments/scheduled?${params.toString()}`
      );

      if (response.data?.success) {
        setAppointments(response.data.data || []);
      } else {
        setAppointments([]);
        toast.error(response.data?.message || "Scheduled appointments load nahi ho paaye.");
      }
    } catch (error: any) {
      console.error(error);
      setAppointments([]);
      toast.error(error.response?.data?.message || "Scheduled appointments load nahi ho paaye.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScheduledAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, statusFilter]);

  const filteredAppointments = useMemo(() => {
    const q = searchText.toLowerCase().trim();
    if (!q) return appointments;

    return appointments.filter((appt) =>
      [
        appt.booking_id,
        appt.fullName,
        appt.phone,
        appt.email,
        appt.department,
        appt.doctor_name,
        appt.appointmentDate,
        appt.appointmentTime,
        appt.status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [appointments, searchText]);

  const stats = useMemo(() => {
    return {
      total: appointments.length,
      waiting: appointments.filter((a) => a.status === "WAITING").length,
      running: appointments.filter((a) => a.status === "IN_CONSULTATION").length,
      completed: appointments.filter((a) => a.status === "COMPLETED").length,
      missed: appointments.filter(
        (a) => a.status === "PATIENT_MISSED" || a.status === "DOCTOR_NO_SHOW" || a.status === "CLINIC_MISSED" || a.status === "MISSED"
      ).length,
    };
  }, [appointments]);

  const getFileUrl = (url?: string | null) => {
    if (!url) return "#";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${backendUrl}${url}`;
  };

  const getStatusBadge = (status: string) => {
    const base = "inline-flex rounded-full px-3 py-1 text-xs font-black uppercase tracking-wider";

    if (status === "SCHEDULED") return `${base} bg-blue-100 text-blue-700`;
    if (status === "DOCTOR_ASSIGNED") return `${base} bg-cyan-100 text-cyan-700`;
    if (status === "LINK_READY") return `${base} bg-indigo-100 text-indigo-700`;
    if (status === "WAITING") return `${base} bg-yellow-100 text-yellow-700`;
    if (status === "IN_CONSULTATION") return `${base} bg-purple-100 text-purple-700`;
    if (status === "COMPLETED") return `${base} bg-emerald-100 text-emerald-700`;
    if (status === "PATIENT_MISSED" || status === "MISSED") return `${base} bg-red-100 text-red-700`;
    if (status === "DOCTOR_NO_SHOW") return `${base} bg-orange-100 text-orange-700`;
    if (status === "CLINIC_MISSED") return `${base} bg-red-100 text-red-700`;

    return `${base} bg-gray-100 text-gray-700`;
  };

  const isFinalStatus = (appt: ScheduledAppointment) => {
    return ["COMPLETED", "PATIENT_MISSED", "DOCTOR_NO_SHOW", "CLINIC_MISSED", "MISSED", "CANCELLED", "REJECTED"].includes(
      appt.status
    );
  };

  const isBothJoined = (appt: ScheduledAppointment) => {
    return appt.patient_join_status === "JOINED" && appt.doctor_join_status === "JOINED";
  };

  const canJoin = (appt: ScheduledAppointment) => {
    return !isFinalStatus(appt) && !!appt.video_link && (Number(appt.canJoinNow) === 1 || appt.status === "IN_CONSULTATION");
  };

  const canStartConsultation = (appt: ScheduledAppointment) => {
    return !isFinalStatus(appt) && isBothJoined(appt) && appt.status !== "IN_CONSULTATION";
  };

  const canComplete = (appt: ScheduledAppointment) => {
    return appt.status === "IN_CONSULTATION" && isBothJoined(appt);
  };

  const canMarkMissed = (appt: ScheduledAppointment) => {
    return !isFinalStatus(appt) && !isBothJoined(appt);
  };

  const shouldShowConsultationBox = (appt: ScheduledAppointment) => {
    return appt.status === "IN_CONSULTATION" || appt.status === "COMPLETED";
  };

  const shouldShowMissedBox = (appt: ScheduledAppointment) => {
    return appt.status === "PATIENT_MISSED" || appt.status === "DOCTOR_NO_SHOW" || appt.status === "CLINIC_MISSED" || appt.status === "MISSED";
  };

  const openDetails = (appt: ScheduledAppointment) => {
    setSelectedAppt(appt);
    setDoctorName(appt.doctor_name || "");
    setDoctorEmail(appt.doctor_email || "");
    setConsultationNotes(appt.consultation_notes || "");
    setPrescriptionText(appt.prescription_text || "");
    setMissedReason(appt.missed_reason || "");
    setShowDetails(true);
  };

  const closeDetails = () => {
    setSelectedAppt(null);
    setShowDetails(false);
    setDoctorName("");
    setDoctorEmail("");
    setConsultationNotes("");
    setPrescriptionText("");
    setMissedReason("");
  };

  const refreshSelectedAppointment = async (id: string) => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/appointments/scheduled/${id}`);
      if (response.data?.success) {
        const fresh = response.data.data;
        setSelectedAppt(fresh);
        setDoctorName(fresh.doctor_name || "");
        setDoctorEmail(fresh.doctor_email || "");
        setConsultationNotes(fresh.consultation_notes || "");
        setPrescriptionText(fresh.prescription_text || "");
        setMissedReason(fresh.missed_reason || "");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const afterAction = async (id: string) => {
    await fetchScheduledAppointments();
    await refreshSelectedAppointment(id);
  };

  const assignDoctor = async (apptId: string) => {
    if (!doctorName.trim()) {
      toast.error("Doctor name likhna zaroori hai.");
      return;
    }

    try {
      setActionLoadingId(apptId);

      const response = await axios.put(
        `${backendUrl}/api/admin/appointments/scheduled/${apptId}/doctor`,
        {
          doctorName: doctorName.trim(),
          doctorEmail: doctorEmail.trim(),
        }
      );

      toast.success(response.data?.message || "Doctor assign ho gaya.");
      await afterAction(apptId);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Doctor assign nahi ho paaya.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const generateVideoLink = async (appt: ScheduledAppointment) => {
    try {
      setActionLoadingId(appt.id);

      const response = await axios.put(
        `${backendUrl}/api/admin/appointments/scheduled/${appt.id}/video-link`
      );

      toast.success(response.data?.message || "Video link generate ho gaya.");
      await afterAction(appt.id);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Video link generate nahi ho paaya.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const markJoined = async (appt: ScheduledAppointment, role: "patient" | "doctor") => {
    try {
      setActionLoadingId(appt.id);

      const response = await axios.put(
        `${backendUrl}/api/admin/appointments/scheduled/${appt.id}/join`,
        { role }
      );

      toast.success(response.data?.message || "Join status update ho gaya.");
      await afterAction(appt.id);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Join status update nahi ho paaya.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const startConsultation = async (appt: ScheduledAppointment) => {
    try {
      setActionLoadingId(appt.id);

      const response = await axios.put(
        `${backendUrl}/api/admin/appointments/scheduled/${appt.id}/start`
      );

      toast.success(response.data?.message || "Consultation start ho gaya.");
      await afterAction(appt.id);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Consultation start nahi ho paaya.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const completeConsultation = async (appt: ScheduledAppointment) => {
    if (!consultationNotes.trim() && !prescriptionText.trim()) {
      toast.error("Complete karne se pehle consultation notes ya prescription likhein.");
      return;
    }

    try {
      setActionLoadingId(appt.id);

      const response = await axios.put(
        `${backendUrl}/api/admin/appointments/scheduled/${appt.id}/complete`,
        {
          notes: consultationNotes.trim(),
          prescriptionText: prescriptionText.trim(),
        }
      );

      toast.success(response.data?.message || "Consultation complete ho gaya.");
      await afterAction(appt.id);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "Consultation complete nahi ho paaya.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const markNoShow = async (appt: ScheduledAppointment, type: "PATIENT_MISSED" | "DOCTOR_NO_SHOW") => {
    try {
      setActionLoadingId(appt.id);

      const response = await axios.put(
        `${backendUrl}/api/admin/appointments/scheduled/${appt.id}/no-show`,
        {
          type,
          reason:
            missedReason.trim() ||
            (type === "DOCTOR_NO_SHOW"
              ? "Patient joined tha, lekin doctor consultation window me join nahi hua."
              : "Patient consultation window me join nahi hua."),
        }
      );

      toast.success(response.data?.message || "No-show update ho gaya.");
      await afterAction(appt.id);
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || "No-show update nahi ho paaya.");
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="mb-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h1 className="text-2xl font-black text-gray-900 md:text-3xl">
              Scheduled Appointments
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Approved online video consultations, doctor assignment aur consultation flow manage karein.
            </p>
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-700 shadow-sm outline-none"
            />

            <button
              type="button"
              onClick={() => setDateFilter("")}
              className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-700 shadow-sm hover:bg-gray-50"
            >
              All Dates
            </button>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-700 shadow-sm outline-none"
            >
              <option value="ALL">All Status</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="DOCTOR_ASSIGNED">Doctor Assigned</option>
              <option value="LINK_READY">Link Ready</option>
              <option value="WAITING">Waiting</option>
              <option value="IN_CONSULTATION">In Consultation</option>
              <option value="COMPLETED">Completed</option>
              <option value="PATIENT_MISSED">Patient Missed</option>
              <option value="DOCTOR_NO_SHOW">Doctor No Show</option>
            </select>

            <div className="flex items-center rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <FaSearch className="mr-3 text-gray-400" />
              <input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchScheduledAppointments();
                }}
                placeholder="Search patient, phone, doctor..."
                className="w-full bg-transparent text-sm font-semibold text-gray-700 outline-none sm:w-72"
              />
            </div>

            <button
              onClick={fetchScheduledAppointments}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white shadow hover:bg-emerald-700 disabled:bg-gray-400"
            >
              <FaSyncAlt className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-5">
          <StatCard label="Total" value={stats.total} tone="blue" />
          <StatCard label="Waiting" value={stats.waiting} tone="yellow" />
          <StatCard label="Running" value={stats.running} tone="purple" />
          <StatCard label="Completed" value={stats.completed} tone="emerald" />
          <StatCard label="Missed" value={stats.missed} tone="red" />
        </div>

        {loading ? (
          <div className="flex min-h-[330px] items-center justify-center rounded-2xl border border-gray-200 bg-white text-emerald-700 shadow-sm">
            <div className="flex items-center gap-3 text-lg font-bold animate-pulse">
              <FaSyncAlt className="animate-spin" />
              Loading scheduled appointments...
            </div>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white py-20 text-center shadow-sm">
            <FaCalendarCheck className="mx-auto mb-4 text-gray-300" size={60} />
            <h2 className="text-xl font-bold text-gray-700">No Scheduled Appointments</h2>
            <p className="mt-1 text-gray-500">
              Is date/status ke according koi approved appointment nahi mila.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="hidden grid-cols-12 gap-4 border-b border-gray-100 bg-gray-50 px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-500 xl:grid">
              <div className="col-span-3">Patient</div>
              <div className="col-span-2">Appointment</div>
              <div className="col-span-2">Doctor</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>

            <div className="divide-y divide-gray-100">
              {filteredAppointments.map((appt) => (
                <div
                  key={appt.id}
                  className="grid grid-cols-1 gap-4 px-5 py-5 transition hover:bg-gray-50 xl:grid-cols-12 xl:items-center xl:px-6"
                >
                  <div className="xl:col-span-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                        <FaUser />
                      </div>
                      <div>
                        <h3 className="font-extrabold text-gray-900">
                          {appt.fullName || "Patient"}
                        </h3>
                        <p className="mt-1 text-xs font-bold text-gray-400">
                          {appt.booking_id || "No Booking ID"}
                        </p>
                        <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                          <FaPhone className="text-xs" />
                          {appt.phone || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="xl:col-span-2">
                    <p className="flex items-center gap-2 font-bold text-gray-800">
                      <FaCalendarCheck className="text-blue-500" />
                      {appt.appointmentDate}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-500">
                      <FaClock className="text-blue-400" />
                      {appt.appointmentTime}
                    </p>
                    <p className="mt-1 flex items-center gap-2 text-sm font-semibold text-gray-500">
                      <FaStethoscope className="text-purple-500" />
                      {appt.department || "N/A"}
                    </p>
                  </div>

                  <div className="xl:col-span-2">
                    <p className="flex items-center gap-2 font-bold text-gray-800">
                      <FaUserMd className="text-emerald-500" />
                      {appt.doctor_name || "Not Assigned"}
                    </p>
                    <p className="mt-1 text-xs font-bold text-gray-500">
                      Patient: {appt.patient_join_status || "NOT_JOINED"}
                    </p>
                    <p className="mt-1 text-xs font-bold text-gray-500">
                      Doctor: {appt.doctor_join_status || "NOT_JOINED"}
                    </p>
                  </div>

                  <div className="xl:col-span-2">
                    <span className={getStatusBadge(appt.status)}>{appt.status}</span>
                    <p className="mt-2 text-xs font-bold text-gray-500">
                      Link: {Number(appt.canJoinNow) === 1 ? "Active" : appt.video_link ? "Inactive" : "Not Generated"}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2 xl:col-span-3 xl:justify-end">
                    <button
                      type="button"
                      onClick={() => openDetails(appt)}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50"
                    >
                      <FaEye />
                      Details
                    </button>

                    {appt.video_link ? (
                      <a
                       href={`${appt.video_link}?role=doctor`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700"
                      >
                        <FaVideo />
                        Open Call
                      </a>
                    ) : (
                      <button
                        type="button"
                        disabled={actionLoadingId === appt.id}
                        onClick={() => generateVideoLink(appt)}
                        className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:bg-gray-400"
                      >
                        <FaLink />
                        Video Link
                      </button>
                    )}

                    <button
                      type="button"
                      disabled={actionLoadingId === appt.id || !canStartConsultation(appt)}
                      onClick={() => startConsultation(appt)}
                      className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-4 py-2 text-xs font-bold text-white hover:bg-purple-700 disabled:bg-gray-400"
                    >
                      <FaPlay />
                      Start
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showDetails && selectedAppt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-extrabold text-gray-900">
                    Scheduled Appointment Details
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Doctor assign, video link, join status, notes aur prescription manage karein.
                  </p>
                </div>
                <button
                  onClick={closeDetails}
                  className="rounded-full bg-gray-100 p-3 text-gray-600 hover:bg-gray-200"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-4">
                <InfoCard label="Booking ID" value={selectedAppt.booking_id || "N/A"} />
                <InfoCard label="Status" value={selectedAppt.status} />
                <InfoCard label="Patient Join" value={selectedAppt.patient_join_status || "NOT_JOINED"} />
                <InfoCard label="Doctor Join" value={selectedAppt.doctor_join_status || "NOT_JOINED"} />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InfoCard label="Patient" value={selectedAppt.fullName} />
                <InfoCard label="Mobile" value={selectedAppt.phone} />
                <InfoCard label="Email" value={selectedAppt.email || "N/A"} />
                <InfoCard label="Department" value={selectedAppt.department || "N/A"} />
                <InfoCard label="Appointment Date" value={selectedAppt.appointmentDate} />
                <InfoCard label="Appointment Time" value={selectedAppt.appointmentTime} />
                <InfoCard label="Gender" value={selectedAppt.gender || "N/A"} />
                <InfoCard label="Age" value={selectedAppt.age || "N/A"} />

                <div className="rounded-xl bg-gray-50 p-4 md:col-span-2">
                  <p className="text-xs font-bold uppercase text-gray-400">Symptoms</p>
                  <p className="text-sm font-medium leading-6 text-gray-700">
                    {selectedAppt.symptoms || "No symptoms added."}
                  </p>
                </div>

                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 md:col-span-2">
                  <p className="mb-3 text-sm font-extrabold text-emerald-700">
                    Assign Doctor
                  </p>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <input
                      value={doctorName}
                      onChange={(e) => setDoctorName(e.target.value)}
                      placeholder="Doctor Name"
                      className="rounded-xl border border-emerald-100 px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-emerald-200"
                    />
                    <input
                      value={doctorEmail}
                      onChange={(e) => setDoctorEmail(e.target.value)}
                      placeholder="Doctor Email optional"
                      className="rounded-xl border border-emerald-100 px-4 py-3 text-sm font-bold outline-none focus:ring-2 focus:ring-emerald-200"
                    />
                  </div>

                  <button
                    onClick={() => assignDoctor(selectedAppt.id)}
                    disabled={actionLoadingId === selectedAppt.id || isFinalStatus(selectedAppt)}
                    className="mt-3 inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-700 disabled:bg-gray-400"
                  >
                    <FaUserMd />
                    Save Doctor
                  </button>
                </div>

                <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 md:col-span-2">
                  <p className="mb-3 text-sm font-extrabold text-blue-700">
                    Video Consultation Link
                  </p>

                  {selectedAppt.video_link ? (
                    <div className="space-y-3">
                      <p className="break-all rounded-xl bg-white p-3 text-sm font-bold text-blue-800">
                        {selectedAppt.video_link}
                      </p>
                      <p className="text-xs font-bold text-blue-700">
                        Active From: {selectedAppt.link_active_from || "N/A"} | Active Until:{" "}
                        {selectedAppt.link_active_until || "N/A"}
                      </p>
                      <a
                        href={selectedAppt.video_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700"
                      >
                        <FaVideo />
                        Open Video Room
                      </a>
                    </div>
                  ) : (
                    <button
                      onClick={() => generateVideoLink(selectedAppt)}
                      disabled={actionLoadingId === selectedAppt.id || isFinalStatus(selectedAppt)}
                      className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      <FaLink />
                      Generate Video Link
                    </button>
                  )}
                </div>

                <div className="rounded-xl border border-yellow-100 bg-yellow-50 p-4 md:col-span-2">
                  <p className="mb-3 text-sm font-extrabold text-yellow-700">
                    Join Controls
                  </p>

                  <p className="mb-3 text-xs font-bold text-yellow-800">
                    Join window appointment time se 5 minute pehle se 10 minute baad tak active rahega.
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => markJoined(selectedAppt, "patient")}
                      disabled={
                        actionLoadingId === selectedAppt.id ||
                        !canJoin(selectedAppt) ||
                        selectedAppt.patient_join_status === "JOINED"
                      }
                      className="inline-flex items-center gap-2 rounded-xl bg-yellow-500 px-5 py-3 text-sm font-bold text-white hover:bg-yellow-600 disabled:bg-gray-400"
                    >
                      <FaUserCheck />
                      Patient Joined
                    </button>

                    <button
                      onClick={() => markJoined(selectedAppt, "doctor")}
                      disabled={
                        actionLoadingId === selectedAppt.id ||
                        !canJoin(selectedAppt) ||
                        selectedAppt.doctor_join_status === "JOINED"
                      }
                      className="inline-flex items-center gap-2 rounded-xl bg-cyan-600 px-5 py-3 text-sm font-bold text-white hover:bg-cyan-700 disabled:bg-gray-400"
                    >
                      <FaUserMd />
                      Doctor Joined
                    </button>

                    <button
                      onClick={() => startConsultation(selectedAppt)}
                      disabled={actionLoadingId === selectedAppt.id || !canStartConsultation(selectedAppt)}
                      className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-5 py-3 text-sm font-bold text-white hover:bg-purple-700 disabled:bg-gray-400"
                    >
                      <FaPlay />
                      Start Consultation
                    </button>
                  </div>
                </div>

                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-xs font-bold uppercase text-gray-400">Medical Report</p>
                  {selectedAppt.report_url ? (
                    <a
                      href={getFileUrl(selectedAppt.report_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-gray-800 px-4 py-2 text-sm font-bold text-white hover:bg-gray-900"
                    >
                      <FaDownload />
                      Open Report
                    </a>
                  ) : (
                    <p className="mt-2 font-bold text-gray-500">No report uploaded.</p>
                  )}
                </div>

                <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <p className="text-xs font-bold uppercase text-blue-500">Payment Proof</p>
                  {selectedAppt.payment_proof_url ? (
                    <a
                      href={getFileUrl(selectedAppt.payment_proof_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700"
                    >
                      <FaEye />
                      Open Payment Proof
                    </a>
                  ) : (
                    <p className="mt-2 font-bold text-red-600">No payment proof.</p>
                  )}
                </div>

                {shouldShowConsultationBox(selectedAppt) && (
                  <>
                    <div className="rounded-xl border border-purple-100 bg-purple-50 p-4 md:col-span-2">
                      <p className="mb-2 flex items-center gap-2 text-sm font-extrabold text-purple-700">
                        <FaNotesMedical />
                        Consultation Notes
                      </p>

                      <textarea
                        value={consultationNotes}
                        onChange={(e) => setConsultationNotes(e.target.value)}
                        rows={5}
                        placeholder="Doctor notes, medicine advice, diet advice..."
                        className="w-full rounded-2xl border border-purple-100 p-4 text-sm font-semibold text-gray-700 outline-none focus:ring-2 focus:ring-purple-200"
                      />
                    </div>

                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 md:col-span-2">
                      <p className="mb-2 text-sm font-extrabold text-emerald-700">
                        Prescription / Medicine Advice
                      </p>

                      <textarea
                        value={prescriptionText}
                        onChange={(e) => setPrescriptionText(e.target.value)}
                        rows={6}
                        placeholder="Medicine name, dosage, timing, diet advice, follow-up date..."
                        className="w-full rounded-2xl border border-emerald-100 p-4 text-sm font-semibold text-gray-700 outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </div>
                  </>
                )}

                {shouldShowMissedBox(selectedAppt) && (
                  <div className="rounded-xl border border-red-100 bg-red-50 p-4 md:col-span-2">
                    <p className="mb-2 text-sm font-extrabold text-red-700">Missed / No-Show Reason</p>
                    <textarea
                      value={missedReason}
                      onChange={(e) => setMissedReason(e.target.value)}
                      rows={3}
                      className="w-full rounded-2xl border border-red-100 p-4 text-sm font-semibold text-gray-700 outline-none focus:ring-2 focus:ring-red-200"
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-end">
                <button
                  disabled={actionLoadingId === selectedAppt.id || !canComplete(selectedAppt)}
                  onClick={() => completeConsultation(selectedAppt)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-bold text-white hover:bg-emerald-700 disabled:bg-gray-400"
                >
                  <FaCheckCircle />
                  Complete Consultation
                </button>

                <button
                  disabled={actionLoadingId === selectedAppt.id || !canMarkMissed(selectedAppt)}
                  onClick={() => markNoShow(selectedAppt, "PATIENT_MISSED")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-3 font-bold text-white hover:bg-red-700 disabled:bg-gray-400"
                >
                  <FaTimesCircle />
                  Patient Missed
                </button>

                <button
                  disabled={actionLoadingId === selectedAppt.id || !canMarkMissed(selectedAppt)}
                  onClick={() => markNoShow(selectedAppt, "DOCTOR_NO_SHOW")}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-600 px-5 py-3 font-bold text-white hover:bg-orange-700 disabled:bg-gray-400"
                >
                  <FaTimesCircle />
                  Doctor No Show
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const StatCard: React.FC<{ label: string; value: number; tone: string }> = ({
  label,
  value,
  tone,
}) => {
  const toneClass =
    tone === "blue"
      ? "border-blue-100 bg-blue-50 text-blue-600"
      : tone === "yellow"
      ? "border-yellow-100 bg-yellow-50 text-yellow-600"
      : tone === "purple"
      ? "border-purple-100 bg-purple-50 text-purple-600"
      : tone === "red"
      ? "border-red-100 bg-red-50 text-red-600"
      : "border-emerald-100 bg-emerald-50 text-emerald-600";

  return (
    <div className={`rounded-2xl border p-5 ${toneClass}`}>
      <p className="text-sm font-bold">{label}</p>
      <h2 className="mt-2 text-4xl font-black text-gray-900">{value}</h2>
    </div>
  );
};

const InfoCard: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <div className="rounded-xl bg-gray-50 p-4">
    <p className="text-xs font-bold uppercase text-gray-400">{label}</p>
    <p className="break-words font-bold text-gray-800">{value || "N/A"}</p>
  </div>
);

export default ScheduledAppointments;